/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2025 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * CourseInformationStyles
 *
 * @file CourseInformationStyles.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import { makeStyles } from '@material-ui/core';
import mixins from '../../../assets/styles/mixins.scss';

const CourseInformationStyles = makeStyles(() => ({
  section: {
    background: mixins.chalkWhiteFont,
    borderRadius: 12,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderWidth: '1px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0,
    boxSizing: 'border-box',
    '& *, &': {
      boxSizing: 'border-box'
    }
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  },
  course: {
    background: mixins.quaternary,
    borderRadius: '8px 8px 0px 0px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderWidth: '1px',
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  },
  titleWrapper: {
    padding: '8px 24px 9px 8px',
    margin: 0,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '33.18px',
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    fontWeight: mixins.fontWeight500
  },
  metaInfoContainer: {
    padding: '8px',
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%'

    // '&:has($instructorContainer)': {
    //   width: '100%'
    // }
    // // width: (props) => (props?.config?.showInstructor ? '100%' : '100%')
  },
  metadetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    color: mixins.zambezi,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: '500',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  metavalue: {
    color: mixins.color_text_primary,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    lineHeight: mixins.lineHeight24,
    fontWeight: mixins.fontWeight600,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  divider: {
    background: 'rgba(13, 0, 77, 0.15)',
    alignSelf: 'stretch',
    flexShrink: 0,
    width: 1
  },
  book: {
    borderRadius: '12px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderWidth: '1px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  },
  figure: {
    borderRadius: '8px',
    flexShrink: 0,
    width: 78,
    height: 104,
    overflow: 'hidden',
    aspectRatio: '78/104',
    position: 'relative'
  },
  img: {
    width: 78,
    height: '100%',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    top: 0,
    objectFit: 'cover',
    aspectRatio: '78/104'
  },
  meta: {
    padding: '12px 0 12px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    flex: 1
  },
  bookInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    flexShrink: 0
  },
  bookTitle: {
    color: mixins.color_text_primary,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '19.2px',
    lineHeight: '120%',
    letterSpacing: '-0.02em',
    fontWeight: mixins.fontWeight500,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  authors: {
    color: mixins.zambezi,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '160%',
    fontWeight: '400',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  buttonSecondary: {
    borderRadius: '1000px',
    borderStyle: 'solid',
    borderColor: mixins.navyColor,
    borderWidth: '2px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'row',
    gap: 0,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  },
  textContainer: {
    padding: '0 8px 2px 8px',
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  label2: {
    color: mixins.navyColor,
    textAlign: 'left',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '100%',
    fontWeight: mixins.fontWeight500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  instructorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  avatar: {
    display: 'flex',
    width: '32px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%'
  },
  instructorName: {
    color: mixins.color_text_primary,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '22px'
  },
  actionButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto'
  },
  iconButton: {
    display: 'flex',
    width: '32px',
    height: '32px',
    padding: '12px',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tertiaryButton: {
    display: 'flex',
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    color: mixins.navyColor,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '22px',
    borderRadius: '1000px',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& span': {
      padding: '0px 8px'
    }
  }
}));

export default CourseInformationStyles;

